import React, { useEffect, useState } from "react";
import { Backdrop, Box, Chip, CircularProgress, Divider, IconButton, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { formatAmount, formatDate } from "../../utils/utils";

const chipSx = (status) =>
  ({
    paid: { bgcolor: "#d1fae5", color: "#065f46" },
    pending: { bgcolor: "#fef3c7", color: "#92400e" },
    failed: { bgcolor: "#fee2e2", color: "#991b1b" },
  }[status] || { bgcolor: "#f3f4f6", color: "#374151" });

function Row({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", py: 0.75 }}>
      <Typography sx={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#111827", textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Box>
  );
}

function CustomerInfo({ userId }) {
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (!userId) return;
    getDoc(doc(db, "users", userId))
      .then((snap) => { if (snap.exists()) setCustomer(snap.data()); })
      .catch(console.error);
  }, [userId]);

  if (!customer) return null;
  const name = [customer.firstname, customer.lastname].filter(Boolean).join(" ") || "—";
  const phone = customer.phone || customer.phoneNumber || "—";

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
        Customer
      </Typography>
      <Row label="Name" value={name} />
      <Row label="Phone" value={phone} />
    </>
  );
}

function BookingDetailModal({ booking, onClose, isAdmin }) {
  if (!booking) return null;
  const b = booking;
  const createdDate =
    b.createdAt?.toDate?.() ||
    (b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : null);
  const slotDate = b.time ? new Date(b.time) : null;

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, alignItems: "flex-start", pt: { xs: 4, md: 8 } }}
    >
      <Paper
        onClick={(e) => e.stopPropagation()}
        elevation={4}
        sx={{ width: { xs: "92vw", sm: 440 }, maxHeight: "80vh", overflowY: "auto", borderRadius: "16px", p: 0, outline: "none" }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 3, py: 2, borderBottom: "1px solid #e5e7eb",
            position: "sticky", top: 0, bgcolor: "#fff", zIndex: 1, borderRadius: "16px 16px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", color: "#5b21b6" }}>
              <CalendarMonthIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Booking Details</Typography>
              <Chip label={b.status || "pending"} size="small" sx={{ ...chipSx(b.status), fontSize: 10, height: 18, mt: 0.25 }} />
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <Row label="Reference" value={b.paymentIntentId || "—"} />
          <Row label="Amount" value={formatAmount(b.amount)} />
          <Row label="Date" value={createdDate ? formatDate(createdDate) : "—"} />
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Booking Info
          </Typography>
          <Row label="Category" value={b.category || "—"} />
          <Row label="Service" value={b.service || "—"} />
          <Row
            label="Slot"
            value={
              slotDate
                ? `${formatDate(slotDate)} · ${slotDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : "—"
            }
          />
          {isAdmin && b.userId && <CustomerInfo userId={b.userId} />}
        </Box>
      </Paper>
    </Backdrop>
  );
}

export default function BookingsTable() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const uid = user?.uid;
  const isAdmin = user?.role === "admin";

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const PER_PAGE = 10;

  useEffect(() => {
    if (!uid) return;
    (async () => {
      try {
        let snap;
        if (isAdmin) {
          snap = await getDocs(
            query(collection(db, "booking-list"), orderBy("createdAt", "desc"))
          );
        } else {
          snap = await getDocs(
            query(collection(db, "booking-list"), where("userId", "==", uid))
          );
        }
        const result = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        result.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
        setDocs(result);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid, isAdmin]);

  const totalPages = Math.ceil(docs.length / PER_PAGE);
  const paged = docs.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#194049" }} />
      </Box>
    );
  }

  const headers = isAdmin
    ? ["Reference", "User", "Category", "Service", "Amount", "Slot", "Status", "Date", ""]
    : ["Reference", "Category", "Service", "Amount", "Slot", "Status", "Date", ""];

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 2 }}>
        {docs.length} booking{docs.length !== 1 ? "s" : ""}
      </Typography>

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {paged.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>
              No bookings found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                  {headers.map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 16px",
                        textAlign: "left",
                        fontSize: 11,
                        color: "#6b7280",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((b) => {
                  const slotDate =
                    b.time
                      ? formatDate(new Date(b.time))
                      : "—";
                  const createdDate =
                    b.createdAt?.toDate?.() ||
                    (b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : null);
                  return (
                    <tr key={b.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#374151", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {b.paymentIntentId || "—"}
                      </td>
                      {isAdmin && (
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>
                          {b.userId?.slice(0, 12)}…
                        </td>
                      )}
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                        {b.category || "—"}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                        {b.service || "—"}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        {formatAmount(b.amount)}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                        {slotDate}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <Chip
                          label={b.status || "pending"}
                          size="small"
                          sx={{ ...chipSx(b.status), fontSize: 11, height: 22, textTransform: "capitalize" }}
                        />
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                        {createdDate ? formatDate(createdDate) : "—"}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <Box
                          onClick={() => setSelected(b)}
                          sx={{
                            display: "inline-flex", alignItems: "center",
                            px: 1.5, py: 0.5, fontSize: 11, fontWeight: 600,
                            color: "#194049", border: "1px solid #194049", borderRadius: "6px",
                            cursor: "pointer", whiteSpace: "nowrap",
                            "&:hover": { bgcolor: "#194049", color: "#fff" },
                            transition: "all 0.15s",
                          }}
                        >
                          View
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
            Page {page + 1} of {Math.max(totalPages, 1)}
          </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {["Prev", "Next"].map((label) => (
                <Box
                  key={label}
                  onClick={() =>
                    setPage((p) =>
                      label === "Prev" ? Math.max(0, p - 1) : Math.min(totalPages - 1, p + 1)
                    )
                  }
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontSize: 12,
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                    bgcolor: "#fff",
                    color: "#374151",
                    "&:hover": { bgcolor: "#f9fafb" },
                    opacity:
                      (label === "Prev" && page === 0) ||
                      (label === "Next" && page >= totalPages - 1)
                        ? 0.4
                        : 1,
                    pointerEvents:
                      (label === "Prev" && page === 0) ||
                      (label === "Next" && page >= totalPages - 1)
                        ? "none"
                        : "auto",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
      </Box>

      {selected && (
        <BookingDetailModal booking={selected} onClose={() => setSelected(null)} isAdmin={isAdmin} />
      )}
    </Box>
  );
}
